/**
 * Translates NJUNS ticket data into API layer shapes, so a *.njuns.com layer is configured and drawn like an ArcGIS layer.
 */

import { esriStyleToIcon } from './EsriStyleToIcon.js';
import { Path } from './Path.js';

/**
 * @typedef {object} NjunsStep
 * @property {string|null} member The assigned member's code.
 * @property {number|null} sequence The step's position in the ticket.
 * @property {string|null} status The step status (PENDING, NTG, COMPLETE).
 * @property {string|null} type The step type name.
 */

/**
 * @typedef {object} MapBounds
 * @property {number} north
 * @property {number} south
 * @property {number} east
 * @property {number} west
 */

/** NJUNS REST path for searching tickets with the view used everywhere else in the app. */
export const NJUNS_TICKET_SEARCH_PATH = 'entities/njuns$Ticket/search?view=ticket-api-view';

/** Ticket statuses an NJUNS layer can filter on, in display order. */
export const NJUNS_TICKET_STATUSES = ['DRAFT', 'OPEN', 'CLOSED', 'CANCELED'];

/** Statuses fetched when a layer has no status filter saved. */
export const NJUNS_DEFAULT_STATUS_FILTER = { DRAFT: true, OPEN: true, CLOSED: false, CANCELED: false };

/** Access control for new NJUNS layers, since tickets still require each map user's own NJUNS sign in. */
export const NJUNS_DEFAULT_ACCESS_CONTROL = 'model_public';

/** Ticket property used to de-duplicate features. */
export const NJUNS_UNIQUE_FIELD = 'id';

/** Feature property the status symbology keys on. */
export const NJUNS_STATUS_STYLE_FIELD = '_njuns_status_style';

/** Field holding the ticket's steps, which are shown as a table rather than a single value. */
export const NJUNS_STEPS_FIELD = 'Steps';

/** Marker fill colors keyed by status style, matching the ticket creator's nearby-ticket markers. */
export const NJUNS_STATUS_COLORS = {
  DRAFT: '#e0e0e0',
  OPEN_OWNER_NEXT: '#4caf50',
  OPEN: '#ffeb3b',
  CLOSED: '#9e9e9e',
  CANCELED: '#616161',
  OTHER: '#90caf9'
};

/** Default fields as [label, getter] pairs, in display order. */
const NJUNS_TICKET_FIELDS = [
  ['Ticket #', (ticket) => ticket.ticketId ?? ticket.ticketNumber],
  ['Status', (ticket) => ticket.status],
  ['Creator', (ticket) => ticket.creator?.code],
  ['Owner', (ticket) => ticket.owner?.code],
  ['NTG Member', (ticket) => ticket.nextToGoMember?.code],
  ['Start Date', (ticket) => formatDateOnly(ticket.startDate)],
  ['Priority', (ticket) => ticket.priority],
  ['Pole/Asset #', (ticket) => ticket.assetId],
  [NJUNS_STEPS_FIELD, (ticket) => sortSteps(ticket.steps)]
];
const NJUNS_TICKET_FIELD_GETTERS = new Map(NJUNS_TICKET_FIELDS);

/** Names of the default NJUNS ticket fields, in display order. */
export const NJUNS_DEFAULT_FIELDS = NJUNS_TICKET_FIELDS.map(([label]) => label);

const NJUNS_DOMAIN = 'njuns.com';
const NJUNS_TEST_HOST = 'test.njuns.com';

/**
 * Whether a layer URL points at NJUNS (njuns.com or any of its subdomains).
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isNjunsLayerUrl(url) {
  const hostname = getHostname(url);
  if (hostname == null) return false;
  return hostname === NJUNS_DOMAIN || hostname.endsWith(`.${NJUNS_DOMAIN}`);
}

/**
 * Whether a layer URL points at the NJUNS test database.
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isNjunsTestUrl(url) {
  return getHostname(url) === NJUNS_TEST_HOST;
}

/**
 * Get the preset configuration for a new NJUNS layer, throwing if the URL is not an NJUNS URL.
 * @param {string} url
 * @returns {{url: string, name: string, options: object}} The layer configuration to fill the editor with.
 */
export function getNjunsLayerDefaults(url) {
  if (!isNjunsLayerUrl(url)) throw new Error('The URL is not an NJUNS URL.');
  return {
    url,
    name: isNjunsTestUrl(url) ? 'NJUNS Tickets (Test)' : 'NJUNS Tickets',
    options: {
      srid: 4326,
      uniqueField: NJUNS_UNIQUE_FIELD,
      fields: NJUNS_DEFAULT_FIELDS.join(', '),
      njuns_statuses: { ...NJUNS_DEFAULT_STATUS_FILTER }
    }
  };
}

/**
 * Get the statuses a layer should fetch from its saved status filter.
 * @param {Record<string, boolean>|null|undefined} statusFilter The layer's `options.njuns_statuses`.
 * @returns {string[]} The selected statuses in display order.
 */
export function getSelectedNjunsStatuses(statusFilter) {
  const filter = statusFilter ?? NJUNS_DEFAULT_STATUS_FILTER;
  return NJUNS_TICKET_STATUSES.filter((status) => filter[status] === true);
}

/**
 * Get the fields a layer should show from its saved fields string, where "*" or an empty value means the default fields.
 * @param {string|null|undefined} fields The layer's `options.fields`.
 * @returns {string[]} The field names in display order.
 */
export function getNjunsDisplayFields(fields) {
  const trimmedFields = typeof fields === 'string' ? fields.trim() : '';
  if (!trimmedFields || trimmedFields === '*') return [...NJUNS_DEFAULT_FIELDS];
  return trimmedFields
    .split(',')
    .map((field) => field.trim())
    .filter(Boolean);
}

/**
 * Build a ticket search for the map bounds and statuses, throwing if a bound is not a number or no statuses are given.
 * @param {MapBounds} bounds The map bounds in degrees.
 * @param {string[]} statuses
 * @returns {object} The request body for NJUNS_TICKET_SEARCH_PATH.
 */
export function buildNjunsTicketSearch(bounds, statuses) {
  const { north, south, east, west } = bounds ?? {};
  if (![north, south, east, west].every(Number.isFinite)) throw new Error('Map bounds must be numbers.');
  if (!Array.isArray(statuses) || statuses.length === 0) throw new Error('At least one ticket status is required.');
  return {
    filter: {
      conditions: [
        {
          group: 'and',
          conditions: [
            { property: 'latitude', operator: '>', value: south },
            { property: 'latitude', operator: '<', value: north },
            { property: 'longitude', operator: '>', value: west },
            { property: 'longitude', operator: '<', value: east },
            { property: 'status', operator: 'in', value: statuses }
          ]
        }
      ]
    },
    view: 'ticket-api-view'
  };
}

/**
 * Whether an NJUNS response or request error says the token is missing, invalid or expired.
 * @param {*} value The parsed NJUNS response or a thrown NjunsRequestError.
 * @returns {boolean}
 */
export function isNjunsAuthError(value) {
  if (value?.status === 401) return true;
  const error = value?.error;
  return typeof error === 'string' && /invalid_token|unauthorized/i.test(error);
}

/**
 * Convert located tickets to point features, throwing if the search result is not a list.
 * @param {object[]} tickets The ticket search result.
 * @param {string[]} fields Default labels use their preset getter, and anything else is read as a dot path like "state.name".
 * @returns {{type: 'FeatureCollection', features: object[]}} Features with the fields plus the unique and status style fields.
 */
export function convertNjunsTicketsToFeatureCollection(tickets, fields) {
  if (!Array.isArray(tickets)) {
    const message = tickets?.error ? `${tickets.error}${tickets.details ? ` - ${tickets.details}` : ''}` : 'Unexpected response';
    throw new Error(`NJUNS ticket search failed: ${message}`);
  }
  const features = tickets
    .filter((ticket) => Number.isFinite(ticket?.latitude) && Number.isFinite(ticket?.longitude))
    .map((ticket) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [ticket.longitude, ticket.latitude] },
      properties: getTicketProperties(ticket, fields)
    }));
  return { type: 'FeatureCollection', features };
}

/**
 * Get the status style key for a ticket, splitting open tickets by whether the owner is next to go.
 * @param {object} ticket
 * @returns {string} A key of NJUNS_STATUS_COLORS.
 */
export function getNjunsStatusStyle(ticket) {
  const { status } = ticket ?? {};
  if (status === 'OPEN') {
    const ownerCompanyId = ticket.owner?.company?.id;
    const isOwnerNext = ownerCompanyId != null && ticket.nextToGoMember?.company?.id === ownerCompanyId;
    return isOwnerNext ? 'OPEN_OWNER_NEXT' : 'OPEN';
  }
  return status in NJUNS_STATUS_COLORS ? status : 'OTHER';
}

/**
 * Get the gvector "unique" symbology that colors ticket markers by status style.
 * @returns {{type: 'unique', property: string, style: string, values: {value: string, vectorOptions: object}[]}} The symbology.
 */
export function getNjunsStatusSymbology() {
  const style = 'esriSMSCircle';
  // Each value needs its own icon, since gvector copies a unique value's vectorOptions straight onto the marker
  const values = Object.entries(NJUNS_STATUS_COLORS).map(([value, fillColor]) => ({
    value,
    vectorOptions: {
      icon: esriStyleToIcon(style, { fillColor, fillOpacity: 1, strokeColor: '#000000', strokeOpacity: 0.8, strokeWidth: 1 })
    }
  }));
  return { type: 'unique', property: NJUNS_STATUS_STYLE_FIELD, style, values };
}

function getTicketProperties(ticket, fields) {
  const properties = {
    [NJUNS_UNIQUE_FIELD]: ticket.id ?? null,
    [NJUNS_STATUS_STYLE_FIELD]: getNjunsStatusStyle(ticket)
  };
  for (const field of fields ?? []) {
    const getValue = NJUNS_TICKET_FIELD_GETTERS.get(field);
    properties[field] = (getValue ? getValue(ticket) : Path.get(ticket, field)) ?? null;
  }
  return properties;
}

/**
 * Sort a ticket's steps by sequence and keep only the values the info window shows.
 * @param {object[]} steps
 * @returns {NjunsStep[]} The sorted steps, or an empty list when the ticket has none.
 */
function sortSteps(steps) {
  if (!Array.isArray(steps)) return [];
  return [...steps]
    .sort((firstStep, secondStep) => (firstStep?.sequence ?? 0) - (secondStep?.sequence ?? 0))
    .map((step) => ({
      member: step?.assignedMember?.code ?? null,
      sequence: step?.sequence ?? null,
      status: step?.status ?? null,
      type: step?.type?.name ?? null
    }));
}

function formatDateOnly(value) {
  if (value == null || value === '') return null;
  return String(value).split(' ')[0];
}

function getHostname(url) {
  if (typeof url !== 'string' || !url.trim()) return null;
  const trimmedUrl = url.trim();
  try {
    const parsedUrl = new URL(trimmedUrl.includes('://') ? trimmedUrl : `https://${trimmedUrl}`);
    return parsedUrl.hostname.toLowerCase();
  } catch {
    return null;
  }
}
